// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// UI
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "95%", sm: "85%", md: "75%", lg: "70%" },
  overflowY: "auto",
  p: 0,
  borderRadius: 3,
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

export default function ModalFinalizarPresupuesto({
  open,
  handleClose,
  idPresupuesto,
  idEvento,
  onFinalized,
}) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);

  // Cargar detalles del presupuesto (incluye cantidadUtilizada)
  const { data, loading, refetch } = useQuery(
    gql`
      query presupuestoEventoDetalles($filter: JSON) {
        presupuestoEventoDetalles(filter: $filter) {
          id
          nombre
          idProducto
          producto {
            id
            nombre
            esElaborado
          }
          cantidadAsignada
          cantidadUtilizada
          precioUnitario
        }
      }
    `,
    {
      variables: { filter: { idPresupuesto: idPresupuesto || null } },
      fetchPolicy: "network-only",
      skip: !open || !idPresupuesto,
    }
  );

  const [finalizar, { loading: finalizando }] = useMutation(
    gql`
      mutation finalizarPresupuesto($input: finalizarPresupuestoEventoInput!) {
        finalizarPresupuestoEvento(input: $input) {
          id
          estado
        }
      }
    `
  );

  const items = useMemo(() => {
    const all = data?.presupuestoEventoDetalles || [];
    return all.filter((d) => d?.idProducto != null);
  }, [data]);
  const [usos, setUsos] = useState({});
  const [ingresarSobrantes, setIngresarSobrantes] = useState(false);

  // Cargar acumulado elaborado por producto para el evento (ProductoEvento)
  const { data: dataProdEvt, loading: loadingProdEvt } = useQuery(
    gql`
      query productoEventos($filter: JSON) {
        productoEventos(filter: $filter) {
          id
          cantidad
          producto {
            id
            nombre
          }
        }
      }
    `,
    {
      variables: { filter: { idPresupuesto: idPresupuesto || null } },
      fetchPolicy: "network-only",
      skip: !open || !idPresupuesto,
    }
  );

  const elaboradoMap = useMemo(() => {
    const map = new Map();
    const list = dataProdEvt?.productoEventos || [];
    // Acumular la cantidad elaborada real por producto
    list.forEach((pe) => {
      const pid = pe?.producto?.id;
      if (pid != null) {
        const key = Number(pid);
        const qty = Number(pe?.cantidad || 0);
        map.set(key, (map.get(key) || 0) + qty);
      }
    });
    return map;
  }, [dataProdEvt]);

  useEffect(() => {
    if (open) {
      // Inicializar cantidades utilizadas con valor existente o 0 sólo para productos
      const init = {};
      items.forEach((d) => {
        init[d.id] = d.cantidadUtilizada ?? 0;
      });
      setUsos(init);
    } else {
      setUsos({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const onChangeUso = (id, value) => {
    const num = Number(value);
    const safe = Math.max(0, Math.round(Number.isNaN(num) ? 0 : num));
    setUsos((prev) => ({ ...prev, [id]: safe }));
  };

  const handleFinalizar = async () => {
      const detalles = items.map((d) => ({
        id: d.id,
        cantidadUtilizada: Math.max(0, Math.round(Number(usos[d.id] ?? 0) || 0)),
      }));
    confirmar({
      title: "Finalizar presupuesto",
      message:
        "¿Está seguro que desea finalizar este presupuesto? Esta acción no se puede deshacer.",
      icon: "warning",
      func: () =>
        finalizar({
          variables: {
            input: { id: idPresupuesto, detalles, ingresarSobrantesStockVenta: ingresarSobrantes },
          },
        })
          .then(async () => {
            handleSnackbar("Presupuesto finalizado", "success");
            if (typeof onFinalized === "function") onFinalized();
            if (typeof refetch === "function") await refetch();
            handleClose();
          })
          .catch(() => handleSnackbar("Error al finalizar presupuesto", "error")),
    });
  };

  const hasSobrantesElaborados = useMemo(() => {
    return items.some((row) => {
      const utilizada = usos[row.id] ?? 0;
      const idProd = row?.producto?.id != null ? Number(row.producto.id) : null;
      const elaborado = idProd != null ? Number(elaboradoMap.get(idProd) || 0) : 0;
      const sobrante = Math.max(0, elaborado - utilizada);
      return row?.producto?.esElaborado && sobrante > 0;
    });
  }, [items, usos, elaboradoMap]);

  const isDisabled = finalizando || !idPresupuesto || items.length === 0;

  return (
    <Modal
      aria-labelledby="modal-finalizar-presupuesto"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          {/* Header del modal */}
          <SoftBox
            sx={{
              borderBottom: "1px solid",
              borderColor: "grey.200",
              p: 3,
              borderRadius: "12px 12px 0 0",
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftBox display="flex" alignItems="center">
                <SoftBox
                  sx={{
                    backgroundColor: "rgba(40, 167, 69, 0.1)",
                    borderRadius: "50%",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2
                  }}
                >
                  <Icon sx={{ fontSize: "1.5rem", color: "success.main" }}>
                    done_all
                  </Icon>
                </SoftBox>
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Finalizar Presupuesto
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                    Registre las cantidades utilizadas para completar el evento
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              <Tooltip title="Cerrar" placement="top">
                <Icon 
                  fontSize="medium" 
                  onClick={handleClose} 
                  sx={{ 
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { color: "text.primary", transform: "scale(1.1)" },
                    transition: "all 0.2s ease"
                  }}
                >
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>

          {/* Contenido del modal */}
          <SoftBox p={3}>
            <SoftBox
              sx={{
                backgroundColor: "info.lighter",
                border: "1px solid",
                borderColor: "info.main",
                borderRadius: 2,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center"
              }}
            >
              <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "info.main" }}>
                info
              </Icon>
              <SoftTypography variant="body2" color="info.main">
                Ingrese la cantidad utilizada por cada ítem. Al finalizar, el presupuesto quedará
                marcado como completado y se actualizará el inventario correspondiente.
              </SoftTypography>
            </SoftBox>

            {loading ? (
              <SoftBox 
                py={6} 
                display="flex" 
                flexDirection="column"
                justifyContent="center" 
                alignItems="center"
                sx={{
                  backgroundColor: "grey.50",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "grey.300"
                }}
              >
                <CircularProgress size={32} color="primary" />
                <SoftTypography variant="caption" color="text" sx={{ mt: 2 }}>
                  Cargando información del presupuesto...
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox>
                {items.length === 0 ? (
                  <SoftBox 
                    py={6} 
                    display="flex" 
                    flexDirection="column"
                    justifyContent="center" 
                    alignItems="center"
                    sx={{
                      backgroundColor: "grey.50",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "grey.300"
                    }}
                  >
                    <Icon sx={{ fontSize: "3rem", color: "grey.400", mb: 2 }}>
                      inventory_2
                    </Icon>
                    <SoftTypography variant="h6" color="text" sx={{ opacity: 0.7 }}>
                      Sin productos asignados
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                      No hay productos asignados en este presupuesto.
                    </SoftTypography>
                  </SoftBox>
                ) : (
                  <SoftBox>
                    <SoftBox 
                      mb={2} 
                      pb={2} 
                      sx={{ 
                        borderBottom: "2px solid", 
                        borderColor: "grey.200" 
                      }}
                    >
                      <SoftBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ fontSize: "1.3rem", mr: 1, color: "primary.main" }}>
                          assignment
                        </Icon>
                        <SoftTypography variant="subtitle1" fontWeight="bold">
                          Productos del Presupuesto ({items.length})
                        </SoftTypography>
                      </SoftBox>
                      <SoftTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                        Ajuste las cantidades utilizadas para cada producto
                      </SoftTypography>
                    </SoftBox>
                    
                    <Grid container spacing={2}>
                      {items.map((row, index) => {
                        const asignada = row.cantidadAsignada ?? 0;
                        const utilizada = usos[row.id] ?? 0;
                        const idProd = row?.producto?.id != null ? Number(row.producto.id) : null;
                        const elaborado = idProd != null ? Number(elaboradoMap.get(idProd) || 0) : 0;
                        const sobranteElaborado = Math.max(0, elaborado - utilizada);
                        const sobrante = sobranteElaborado;
                        
                        return (
                          <Grid key={row.id} item xs={12}>
                            <Card 
                              sx={{
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                                "&:hover": {
                                  borderColor: "primary.main",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                                },
                                transition: "all 0.2s ease"
                              }}
                            >
                              <SoftBox p={1.5}>
                                <Grid container spacing={1.5} alignItems="center">
                                  <Grid item xs={12} md={7}>
                                    <SoftBox display="flex" alignItems="center">
                                      <SoftBox
                                        sx={{
                                          backgroundColor: row?.producto?.esElaborado ? "rgba(255, 152, 0, 0.1)" : "rgba(76, 175, 80, 0.1)",
                                          borderRadius: "50%",
                                          p: 0.5,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          minWidth: "28px",
                                          height: "28px",
                                          mr: 1.5
                                        }}
                                      >
                                        <Icon sx={{ 
                                          fontSize: "0.9rem", 
                                          color: row?.producto?.esElaborado ? "warning.main" : "success.main" 
                                        }}>
                                          {row?.producto?.esElaborado ? "precision_manufacturing" : "restaurant"}
                                        </Icon>
                                      </SoftBox>
                                      <SoftBox>
                                        <SoftTypography variant="body2" fontWeight="medium">
                                          {row?.nombre || row?.producto?.nombre || `Detalle #${row?.id}`}
                                        </SoftTypography>
                                        <SoftBox display="flex" alignItems="center" gap={1} mt={0.3}>
                                          <SoftTypography variant="caption" color="text">
                                            Asignada: {asignada}
                                          </SoftTypography>
                                          {elaborado > 0 && (
                                            <SoftTypography variant="caption" color="text">
                                              • Elaborado: {elaborado}
                                            </SoftTypography>
                                          )}
                                          {sobrante > 0 && (
                                            <SoftTypography variant="caption" color="warning.main" fontWeight="medium">
                                              • Sobrante: {sobrante}
                                            </SoftTypography>
                                          )}
                                        </SoftBox>
                                      </SoftBox>
                                    </SoftBox>
                                  </Grid>
                                  <Grid item xs={12} md={5}>
                                    <SoftBox display="flex" justifyContent="flex-end">
                                      <SoftBox sx={{ minWidth: 120 }}>
                                        <SoftBox mb={0.5}>
                                          <SoftTypography component="label" variant="caption" fontWeight="bold" color="text">
                                            Cantidad utilizada
                                          </SoftTypography>
                                        </SoftBox>
                                        <SoftInput
                                          type="number"
                                          name={`utilizada-${row.id}`}
                                          value={utilizada}
                                          onChange={(e) => onChangeUso(row.id, e.target.value)}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                              backgroundColor: "white"
                                            },
                                            "& .MuiOutlinedInput-input": {
                                              py: 1
                                            }
                                          }}
                                          inputProps={{ min: 0, step: 1 }}
                                          fullWidth
                                        />
                                      </SoftBox>
                                    </SoftBox>
                                  </Grid>
                                </Grid>
                              </SoftBox>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </SoftBox>
                )}
              </SoftBox>
            )}

            {/* Sección de opciones y botones */}
            <SoftBox 
              mt={4}
              pt={3}
              sx={{
                borderTop: "1px solid",
                borderColor: "grey.200"
              }}
            >
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <SoftBox>
                  {hasSobrantesElaborados && (
                    <SoftBox
                      sx={{
                        backgroundColor: "warning.lighter",
                        border: "1px solid",
                        borderColor: "warning.main",
                        borderRadius: 2,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <Icon sx={{ fontSize: "1.2rem", mr: 3, color: "warning.main" }}>
                        inventory
                      </Icon>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ingresarSobrantes}
                            onChange={(e) => setIngresarSobrantes(e.target.checked)}
                            color="warning"
                          />
                        }
                        label={
                          <SoftTypography variant="body2" color="warning.main" fontWeight="medium">
                            Ingresar sobrantes al stock de venta
                          </SoftTypography>
                        }
                      />
                    </SoftBox>
                  )}
                </SoftBox>
                
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <SoftButton
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "medium",
                      px: 3,
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)"
                      },
                      transition: "all 0.2s ease"
                    }}
                  >
                    <Icon sx={{ fontWeight: "light", mr: 1 }}>cancel</Icon>
                    Cancelar
                  </SoftButton>
                  <SoftButton
                    color="success"
                    disabled={isDisabled}
                    onClick={handleFinalizar}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "medium",
                      px: 3,
                      minWidth: 140,
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(40, 167, 69, 0.3)"
                      },
                      "&:disabled": {
                        opacity: 0.6
                      },
                      transition: "all 0.2s ease"
                    }}
                  >
                    {finalizando ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Icon sx={{ fontWeight: "light", mr: 1 }}>done_all</Icon>
                    )}
                    Finalizar Presupuesto
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalFinalizarPresupuesto.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPresupuesto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idEvento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFinalized: PropTypes.func,
};
